import { gql, useApolloClient, useMutation } from '@apollo/client';
import { useToast } from '@chakra-ui/react';

import { group_joined, group_left, membership_group_left } from '@/analytics';
import { useCurrent } from '@/hooks/useCurrent';
import { logError } from '@/imports/logging/ClientLogger';

import {
  useEditCurrentUserGroupMembership_CreateGroupLinkMutation,
  useEditCurrentUserGroupMembership_CreateGroupLinkMutationVariables,
} from './graphql-types/useEditCurrentUserGroupMembership_CreateGroupLinkMutation';
import {
  useEditCurrentUserGroupMembership_DeleteGroupLinkMutation,
  useEditCurrentUserGroupMembership_DeleteGroupLinkMutationVariables,
} from './graphql-types/useEditCurrentUserGroupMembership_DeleteGroupLinkMutation';
import { useEditCurrentUserGroupMembership_GroupFragment } from './graphql-types/useEditCurrentUserGroupMembership_GroupFragment';

const FOLLOW_GROUP = gql`
  mutation useEditCurrentUserGroupMembership_CreateGroupLinkMutation(
    $input: CreateUserGroupLinkInput!
  ) {
    createUserGroupLink(input: $input) {
      data {
        _id
        user {
          _id
          userOnboardingChecklistState {
            userHasJoinedAGroup
          }
        }
        group {
          _id
          currentUserGroupLink {
            _id
          }
          parentGroup {
            _id
            currentUserGroupLink {
              _id
            }
          }
        }
      }
    }
  }
`;

const UNFOLLOW_GROUP = gql`
  mutation useEditCurrentUserGroupMembership_DeleteGroupLinkMutation(
    $input: DeleteUserGroupLinkInput!
  ) {
    deleteUserGroupLink(input: $input) {
      data {
        _id
        user {
          _id
          userOnboardingChecklistState {
            userHasJoinedAGroup
          }
        }
        group {
          currentUserGroupLink {
            _id
          }
          subgroups {
            groupNodes {
              group {
                _id
                currentUserGroupLink {
                  _id
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const useEditCurrentUserGroupMembership = (
  group: useEditCurrentUserGroupMembership_GroupFragment
): {
  createFollow: () => Promise<void>;
  deleteFollow: () => Promise<void>;
  userLoading: boolean;
  createFollowLoading: boolean;
  deleteFollowLoading: boolean;
} => {
  const current = useCurrent();
  const [createUserGroupLink, { loading: createFollowLoading }] = useMutation<
    useEditCurrentUserGroupMembership_CreateGroupLinkMutation,
    useEditCurrentUserGroupMembership_CreateGroupLinkMutationVariables
  >(FOLLOW_GROUP, {
    refetchQueries: [
      'GroupMembers_GroupUsersSearch',
      'VanillaNavigation_Query',
      'GroupSectionPage_GroupQuery',
      'GroupActivityPane_groupQuery',
      'GroupPageLayout_groupQuery',
      'postFeedV2',
    ],
    awaitRefetchQueries: true,
  });
  const [deleteUserGroupLink, { loading: deleteFollowLoading }] = useMutation<
    useEditCurrentUserGroupMembership_DeleteGroupLinkMutation,
    useEditCurrentUserGroupMembership_DeleteGroupLinkMutationVariables
  >(UNFOLLOW_GROUP, {
    // Only refetch queries that don't require group access
    // Group-specific queries are removed because user can't find the group after leaving
    refetchQueries: [
      'VanillaNavigation_Query',
      // Note: Group-specific queries removed to avoid permission errors after leaving
      // Cache is manually updated in deleteFollow function
    ],
    awaitRefetchQueries: false, 
  });

  const apolloClient = useApolloClient();

  const toast = useToast();

  const createFollow = async (): Promise<void> => {
    if (!current.user?._id) {
      return;
    }
    try {
      await createUserGroupLink({
        variables: {
          input: {
            data: {
              userId: current.user._id,
              groupId: group._id,
            },
          },
        },
      });

      group_joined({
        entity_type: 'group',
        entity_id: group._id,
        group_id: group._id,
        group_name: group.name,
        group_join_source: 'button',
        group_change_initiated_by: 'self',
        group_change_is_user_action: true,
      });

      if (
        !!group.parentGroup?._id &&
        !group.parentGroup.currentUserGroupLink?._id
      ) {
        toast({
          title: `Successfully joined group "${group.name}" and its parent group "${group.parentGroup.name}".`,
          status: 'success',
        });
      } else {
        toast({
          title: `Successfully joined group "${group.name}".`,
          status: 'success',
        });
      }
    } catch (err) {
      toast({
        title: `Failed to join group "${group.name}".`,
        status: 'error',
      });
      logError(err);
    }
  };

  const deleteFollow = async (): Promise<void> => {
    try {
      const result = await deleteUserGroupLink({
        variables: {
          input: { id: group.currentUserGroupLink?._id },
        },
      }).catch((err: any) => {
        const isPermissionErrorFromRefetch = err?.graphQLErrors?.some(
          (e: any) =>
            (e?.message?.includes('not allowed to find group') ||
              e?.extensions?.exception?.name === 'PermissionError') &&
            e?.path?.[0] === 'group' 
        );

        if (isPermissionErrorFromRefetch) {
          return;
        }
        throw err;
      });

      const followedSubgroups = (
        result?.data?.deleteUserGroupLink?.data?.group?.subgroups?.groupNodes ||
        []
      )
        .filter((wrapper) => !!wrapper.group.currentUserGroupLink?._id)
        .map((wrapper) => wrapper.group._id);

      apolloClient.cache.modify({
        id: apolloClient.cache.identify({
          id: group._id,
          __typename: 'Group',
        }),
        fields: {
          currentUserGroupLink() {
            return null;
          },
        },
      });
      followedSubgroups.forEach((subgroup) => {
        apolloClient.cache.modify({
          id: apolloClient.cache.identify({
            id: subgroup,
            __typename: 'Group',
          }),
          fields: {
            currentUserGroupLink() {
              return null;
            },
          },
        });
      });
      apolloClient.cache.gc();

      group_left({
        entity_type: 'group',
        entity_id: group._id,
        group_id: group._id,
        group_name: group.name,
        group_leave_source: 'self',
        group_change_initiated_by: 'self',
        group_change_is_user_action: true,
      });
      membership_group_left({
        entity_type: 'group',
        entity_id: group._id,
        group_id: group._id,
        group_name: group.name,
        page_type: 'membership',
        page_section: 'churn_signal',
      });

      if (followedSubgroups.length > 0) {
        toast({
          title: `Successfully left group "${group.name}" and ${followedSubgroups.length} subgroups.`,
          status: 'success',
        });
      } else {
        toast({
          title: `Successfully left group "${group.name}".`,
          status: 'success',
        });
      }
    } catch (err) {
      toast({
        title: `Failed to leave group "${group.name}".`,
        status: 'error',
      });
      logError(err);
    }
  };

  return {
    createFollow,
    deleteFollow,
    userLoading: current.loading,
    createFollowLoading,
    deleteFollowLoading,
  };
};
useEditCurrentUserGroupMembership.groupFragment = gql`
  fragment useEditCurrentUserGroupMembership_GroupFragment on Group {
    _id
    name
    currentUserGroupLink {
      _id
    }
    currentUserGroupPermission {
      canJoin
    }
    parentGroup {
      _id
      name
      currentUserGroupLink {
        _id
      }
    }
  }
`;
